import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import moment from 'moment';
import FileSaver from 'file-saver';
import { toastr } from 'react-redux-toastr';
import CustomButton from '../../../CustomButton';
import DateDropdown from '../../../dump/menuComponents/DateDropdown';
import { getDevicesList } from '../../../../action/DeviceActions';
import { getIFTAReportsList } from '../../../../action/ReportsActions';
import { getIFTAReportCsv } from '../../../../action/FleetAdminActions';
import { renderCustomHeader, tableNoPaginationOptions } from '../../../helpers/table';
import { FaChevronDown, FaDownload, FaSearch } from 'react-icons/fa';

const reportNameOptions = [
    { value: 'ifta_asset', label: 'Interstate Fuel Tax Report (IFTA)' },
    { value: 'ifta_asset_state', label: 'Asset Mileage By State (IFTA)' },
    { value: 'ifta_fleet', label: 'Fleet Mileage By State (IFTA)' },
];
const reportTypeNames = {
    basic: 'Interstate Fuel Tax Report (IFTA)',
    asset: 'Asset Mileage By State (IFTA)',
    fleet: 'Fleet Mileage By State (IFTA)',
    ifta_asset: 'Interstate Fuel Tax Report (IFTA)',
    ifta_asset_state: 'Asset Mileage By State (IFTA)',
    ifta_fleet: 'Fleet Mileage By State (IFTA)',
};
const formatDate = (date) => moment(date).format('MMM D, YYYY');
const formatDateRangeText = (start, end) => `${moment(start).format('M/D/YY')} - ${moment(end).format('M/D/YY')}`;

const getUserLabel = (user) => {
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
    return name || user.email || user.username || user.id;
}

const getVehicleLabel = (device) => device.vehicle_name || device.device_name || device.device_id?.toString();
const getVehicleType = (device) => device.device_type || device.type || device.model || 'N/A';
const getReportName = (type) => reportTypeNames[type] || type;
const isSameValue = (a, b) => String(a) === String(b);
const sanitizeFileName = (name) => String(name || '').replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, '_').toLowerCase();

const getFormattedReports = (reports, vehicleLookup = {}) => reports.flatMap(report => {
    const reportDevices = (report.device_ids || []).map(deviceId => {
        const device = vehicleLookup[deviceId] || vehicleLookup[deviceId?.toString()] || {};
        return {
            value: deviceId,
            label: device.label || deviceId?.toString(),
            type: device.type || 'N/A',
        };
    });

    const reportType = report.report_type || report.ifta_type;

    const formattedReport = {
        id: report.id,
        rowId: `${report.id}-${reportType}`,
        reportName: getReportName(reportType),
        reportType,
        requestedBy: report.user_name,
        requestedByValue: report.user_id,
        dateGenerated: formatDate(report.created_ts),
        generatedAt: report.created_ts,
        dateRange: formatDateRangeText(report.start_date, report.end_date),
        dateRangeStart: report.start_date,
        dateRangeEnd: report.end_date,
        devices: reportDevices,
        devicesCount: reportDevices.length,
        status: report.status,
    };

    if (reportType !== 'ifta_asset') {
        return [formattedReport];
    }

    return [
        formattedReport,
        {
            ...formattedReport,
            rowId: `${report.id}-ifta_asset_state`,
            reportName: getReportName('ifta_asset_state'),
            reportType: 'ifta_asset_state',
        },
    ];
});

const CheckboxFilter = ({ label, value, options, searchPlaceholder, selected, onChange }) => {
    const filterRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');

    const filteredOptions = options.filter(option => option.label.toLowerCase().includes(search.toLowerCase()));
    const allSelected = options.length > 0 && selected.length === options.length;

    useEffect(() => {
        if (!isOpen) return;

        const closeOnOutsideClick = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', closeOnOutsideClick);

        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
        };
    }, [isOpen]);

    const toggleOption = (optionValue) => {
        if (selected.includes(optionValue)) {
            onChange(selected.filter(selectedOption => selectedOption !== optionValue));
        }
        else {
            onChange([...selected, optionValue]);
        }
    }

    const toggleAll = () => {
        onChange(allSelected ? [] : options.map(option => option.value));
    }

    return (
        <div className="download-filter checkbox-filter" ref={filterRef}>
            <label>{label}</label>
            <button
                type="button"
                className={'download-filter-control' + (isOpen ? ' open' : '')}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span>{value}</span>
                <FaChevronDown />
            </button>
            {isOpen &&
                <div className="checkbox-filter-menu">
                    <div className="checkbox-filter-search">
                        <FaSearch />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={searchPlaceholder}
                        />
                    </div>
                    <label className="checkbox-filter-option">
                        <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={!options.length} />
                        <span>Select All</span>
                    </label>
                    {filteredOptions.length ? filteredOptions.map(option => (
                        <label className="checkbox-filter-option" key={option.value}>
                            <input
                                type="checkbox"
                                checked={selected.includes(option.value)}
                                onChange={() => toggleOption(option.value)}
                            />
                            <span>{option.label}</span>
                        </label>
                    )) : (
                        <div className="checkbox-filter-empty">No options available</div>
                    )}
                </div>
            }
        </div>
    );
}

const DownloadReportsModal = ({ onHide }) => {
    const user = useSelector(state => state.user.user);
    const company = useSelector(state => state.company.company);
    const currentUserValue = user.id || user.user_id || user.email;

    const [requestedBy, setRequestedBy] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [reportNames, setReportNames] = useState([]);
    const [requestedByOptions, setRequestedByOptions] = useState([]);
    const [vehicleOptions, setVehicleOptions] = useState([]);
    const [allDownloads, setAllDownloads] = useState([]);
    const [downloads, setDownloads] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [downloadingReportRowId, setDownloadingReportRowId] = useState(null);
    const [startDate, setStartDate] = useState(moment().startOf('month').toDate());
    const [endDate, setEndDate] = useState(moment().endOf('day').toDate());
    const [selectedDevicesReport, setSelectedDevicesReport] = useState(null);
    const [deviceSearch, setDeviceSearch] = useState('');
    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const sortedRequestedByOptions = useMemo(() => {
        const currentUserHasReports = requestedByOptions.some(option => isSameValue(option.value, currentUserValue));

        return [...requestedByOptions].sort((a, b) => {
            if (currentUserHasReports && isSameValue(a.value, currentUserValue)) return -1;
            if (currentUserHasReports && isSameValue(b.value, currentUserValue)) return 1;
            return a.label.localeCompare(b.label);
        });
    }, [currentUserValue, requestedByOptions]);

    const getRequestedByOptionsFromReports = useCallback((reports) => {
        const authorsById = reports.reduce((acc, report) => {
            if (report.requestedByValue && report.requestedBy) {
                acc[report.requestedByValue] = {
                    value: report.requestedByValue,
                    label: report.requestedBy,
                };
            }

            return acc;
        }, {});

        return Object.values(authorsById).sort((a, b) => {
            if (isSameValue(a.value, currentUserValue)) return -1;
            if (isSameValue(b.value, currentUserValue)) return 1;
            return a.label.localeCompare(b.label);
        });
    }, [currentUserValue]);

    const filteredReportDevices = useMemo(() => {
        const search = deviceSearch.trim().toLowerCase();
        const reportDevices = selectedDevicesReport?.devices || [];

        if (!search) return reportDevices;

        return reportDevices.filter(device =>
            device.label.toLowerCase().includes(search) ||
            device.value?.toString().toLowerCase().includes(search)
        );
    }, [deviceSearch, selectedDevicesReport]);

    const filterReportsByClientFilters = useCallback((reports, filters) => reports.filter(report => {
        const matchesRequestedBy = !filters.requestedBy.length || filters.requestedBy.some(userId => isSameValue(userId, report.requestedByValue));
        const matchesVehicles = !filters.vehicles.length || report.devices.some(device => filters.vehicles.some(deviceId => isSameValue(deviceId, device.value)));
        const matchesReportName = !filters.reportNames.length || filters.reportNames.some(reportType => isSameValue(reportType, report.reportType));
        const reportStartDate = moment(report.dateRangeStart);
        const reportEndDate = moment(report.dateRangeEnd);
        const isReportDateRangeInSelectedRange = reportStartDate.isSameOrBefore(filters.endDate, 'day')
            && reportEndDate.isSameOrAfter(filters.startDate, 'day');

        return matchesRequestedBy && matchesVehicles && matchesReportName && isReportDateRangeInSelectedRange;
    }), []);

    useEffect(() => {
        if (!company.company_id) return;

        setRequestedBy([]);
        setVehicles([]);
        setReportNames([]);

        if (isMountedRef.current) {
            setIsLoading(true);
        }

        Promise.all([
            getDevicesList(company.company_id),
            getIFTAReportsList({ company_id: company.company_id }),
        ])
            .then(([devicesRes, reportsRes]) => {
                const devices = devicesRes.data.response.devices || [];
                const nextVehicleOptions = devices
                    .map(device => ({
                        value: device.device_id,
                        label: getVehicleLabel(device),
                        type: getVehicleType(device),
                    }))
                    .filter(option => option.value && option.label)
                    .sort((a, b) => a.label.localeCompare(b.label));
                const nextVehicleLookup = nextVehicleOptions.reduce((acc, vehicle) => {
                    acc[vehicle.value] = vehicle;
                    return acc;
                }, {});
                const reports = reportsRes.data.response?.reports || [];
                const uniqueReports = reports.filter((report, index, arr) => arr.findIndex(item => item.id === report.id) === index);
                const formattedReports = getFormattedReports(uniqueReports, nextVehicleLookup);
                const nextRequestedByOptions = getRequestedByOptionsFromReports(formattedReports);

                if (isMountedRef.current) {
                    setVehicleOptions(nextVehicleOptions);
                    setAllDownloads(formattedReports);
                    setDownloads(formattedReports);
                    setRequestedByOptions(nextRequestedByOptions);
                    setRequestedBy(selected => selected.filter(userId => nextRequestedByOptions.some(option => isSameValue(option.value, userId))));
                }
            })
            .catch(error => {
                console.log('DownloadReportsModal get IFTA reports error:', error);
                if (isMountedRef.current) {
                    setVehicleOptions([]);
                    setAllDownloads([]);
                    setDownloads([]);
                    setRequestedByOptions([]);
                }
            })
            .finally(() => {
                if (isMountedRef.current) {
                    setIsLoading(false);
                }
            });
    }, [company.company_id, getRequestedByOptionsFromReports]);

    const formatFilterValue = (selected, options, defaultText) => {
        if (!selected.length) return defaultText;
        if (selected.length === 1) {
            const option = options.find(item => item.value === selected[0]);
            return option ? option.label : selected[0];
        }
        return `${selected.length} Selected`;
    }

    const applyFilters = () => {
        const filteredReports = filterReportsByClientFilters(allDownloads, {
            requestedBy,
            vehicles,
            reportNames,
            startDate,
            endDate,
        });

        setDownloads(filteredReports);
    }

    const clearFilters = () => {
        setRequestedBy([]);
        setVehicles([]);
        setReportNames([]);
        setStartDate(moment().startOf('month').toDate());
        setEndDate(moment().endOf('day').toDate());
        setDownloads(allDownloads);
    }

    const formatDeviceCount = (cell, row) => (
        <CustomButton
            variant="link"
            prefix="download-devices-link"
            onClick={() => {
                setDeviceSearch('');
                setSelectedDevicesReport(row);
            }}
        >
            {cell}
        </CustomButton>
    );

    const downloadReportCsv = (row) => {
        if (!row?.id || downloadingReportRowId) return;

        setDownloadingReportRowId(row.rowId);

        getIFTAReportCsv({ report_id: row.id })
            .then(res => {
                const blob = new Blob([res.data], { type: 'text/csv' });
                const timeframe = moment(row.dateRangeStart).format('M-D-YY')
                    + (moment(row.dateRangeStart).isSame(row.dateRangeEnd, 'day') ? '' : '_' + moment(row.dateRangeEnd).format('M-D-YY'));
                const fileName = `${sanitizeFileName(row.reportName)}_${timeframe}.csv`;

                FileSaver.saveAs(blob, fileName);
            })
            .catch(() => {
                toastr.error('Unable to export CSV');
            })
            .finally(() => {
                if (isMountedRef.current) {
                    setDownloadingReportRowId(null);
                }
            });
    }

    const formatDownloadAction = (cell, row) => (
        <CustomButton
            variant="primary-outline"
            size="xs"
            prefix="download-csv-button"
            onClick={() => downloadReportCsv(row)}
            disabled={downloadingReportRowId === row.rowId}
        >
            <FaDownload /> CSV
        </CustomButton>
    );

    const sortByString = (a, b, order, sortField) => {
        const valueA = a && sortField ? a[sortField] : a;
        const valueB = b && sortField ? b[sortField] : b;
        const result = String(valueA || '').localeCompare(String(valueB || ''));
        return order === 'asc' ? result : -result;
    }

    return (
        <Modal
            size="xl"
            show
            onHide={onHide}
            className="reports-modal table-modal download-reports-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Downloads
                    <span className="download-reports-count">{downloads.length} Available</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="download-reports-filters">
                    <div className="download-reports-filter-group">
                        <CheckboxFilter
                            label="REQUESTED BY"
                            value={formatFilterValue(requestedBy, sortedRequestedByOptions, 'All Users')}
                            options={sortedRequestedByOptions}
                            searchPlaceholder="Search users..."
                            selected={requestedBy}
                            onChange={setRequestedBy}
                        />
                        <div className="download-filter date-range-filter">
                            <label>DATE RANGE</label>
                            <DateDropdown
                                id="download-reports-date-range"
                                start_date={startDate}
                                end_date={endDate}
                                onSelectDate={(start, end) => {
                                    setStartDate(start);
                                    setEndDate(end);
                                }}
                            />
                        </div>
                        <CheckboxFilter
                            label="VEHICLES"
                            value={formatFilterValue(vehicles, vehicleOptions, 'All Vehicles')}
                            options={vehicleOptions}
                            searchPlaceholder="Search vehicles..."
                            selected={vehicles}
                            onChange={setVehicles}
                        />
                        <CheckboxFilter
                            label="REPORT NAME"
                            value={formatFilterValue(reportNames, reportNameOptions, 'All Reports')}
                            options={reportNameOptions}
                            searchPlaceholder="Search reports..."
                            selected={reportNames}
                            onChange={setReportNames}
                        />
                    </div>
                    <div className="download-reports-filter-actions">
                        <CustomButton variant="primary-outline" prefix="clear-download-filters" onClick={clearFilters} disabled={isLoading}>
                            CLEAR FILTERS
                        </CustomButton>
                        <CustomButton variant="primary" prefix="apply-download-filters" onClick={applyFilters} disabled={isLoading}>
                            APPLY FILTERS
                        </CustomButton>
                    </div>
                </div>

                <div className="manage-users-table custom-table-style data-usage-page-table">
                    <BootstrapTable
                        data={downloads}
                        {...tableNoPaginationOptions}
                        headerContainerClass="rosco-table-header custom-table-header-container"
                    >
                        <TableHeaderColumn dataField="rowId" isKey hidden />
                        <TableHeaderColumn
                            dataField="reportName"
                            dataSort
                            sortFunc={sortByString}
                        >
                            {renderCustomHeader(0, 'Report Name')}
                        </TableHeaderColumn>
                        <TableHeaderColumn dataField="requestedBy" dataSort>
                            {renderCustomHeader(1, 'Requested By')}
                        </TableHeaderColumn>
                        <TableHeaderColumn dataField="dateRange" width="160" dataSort>
                            {renderCustomHeader(2, 'Date Range')}
                        </TableHeaderColumn>
                        <TableHeaderColumn dataField="dateGenerated" width="160" dataSort>
                            {renderCustomHeader(3, 'Date Generated')}
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            dataField="devicesCount"
                            dataFormat={formatDeviceCount}
                            dataAlign="center"
                            columnClassName="download-devices-cell"
                            width="140"
                            dataSort
                        >
                            {renderCustomHeader(4, 'Devices')}
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            dataField="action"
                            dataFormat={formatDownloadAction}
                            dataAlign="center"
                            columnClassName="download-action-cell"
                            width="140"
                        >
                            Action
                        </TableHeaderColumn>
                    </BootstrapTable>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <CustomButton variant="primary-outline" onClick={onHide}>
                    Close
                </CustomButton>
            </Modal.Footer>

            <Modal
                show={!!selectedDevicesReport}
                onHide={() => {
                    setDeviceSearch('');
                    setSelectedDevicesReport(null);
                }}
                className="reports-modal table-modal download-devices-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Devices</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="download-devices-search">
                        <FaSearch />
                        <input
                            type="text"
                            value={deviceSearch}
                            onChange={(e) => setDeviceSearch(e.target.value)}
                            placeholder="Search Assets"
                        />
                    </div>
                    <div className="download-devices-list">
                        {filteredReportDevices.map(device => (
                            <div className="download-device-row" key={device.value}>
                                <span>{device.label}</span>
                                <span>{device.type}</span>
                            </div>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <CustomButton
                        variant="primary-outline"
                        onClick={() => {
                            setDeviceSearch('');
                            setSelectedDevicesReport(null);
                        }}
                    >
                        Close
                    </CustomButton>
                </Modal.Footer>
            </Modal>
        </Modal>
    );
};

export default DownloadReportsModal;
